/**
 * Utility for animating elements when they come into view
 */

export const setupScrollAnimations = (): void => {
  // Check for IntersectionObserver support
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers that don't support IntersectionObserver
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((element) => {
      element.classList.add('visible');
    });
    return;
  }

  const options = {
    root: null, // Use viewport as root
    rootMargin: '0px',
    threshold: 0.1, // Trigger when at least 10% of the element is visible
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        // Once animation is triggered, stop observing
        observer.unobserve(entry.target);
      }
    });
  }, options);

  // Observe all elements with the animate-on-scroll class
  const elements = document.querySelectorAll('.animate-on-scroll');
  elements.forEach((element) => {
    observer.observe(element);
  });
};

/**
 * Apply animation classes to elements
 * @param delay - Optional delay in ms before starting animation
 * @param staggered - Whether to stagger child animations
 */
export const addScrollAnimation = (
  elementRef: React.RefObject<HTMLElement>,
  delay: number = 0,
  staggered: boolean = false
): void => {
  if (!elementRef.current) return;

  const element = elementRef.current;

  // Add the base animation class
  element.classList.add('animate-on-scroll');

  // Apply delay if specified
  if (delay > 0) {
    element.style.transitionDelay = `${delay}ms`;
  }

  // Apply staggered animations to children if requested
  if (staggered) {
    const children = element.children;
    Array.from(children).forEach((child, index) => {
      const childElement = child as HTMLElement;
      childElement.classList.add('animate-on-scroll');
      childElement.style.transitionDelay = `${delay + index * 100}ms`;
    });
  }
};

/**
 * Initialize animations on page load
 */
export const initScrollAnimations = (): void => {
  // Wait for page to load fully
  if (document.readyState === 'complete') {
    setupScrollAnimations();
  } else {
    window.addEventListener('load', setupScrollAnimations);
  }

  // Re-check when content changes (useful for SPAs)
  document.addEventListener('DOMContentLoaded', setupScrollAnimations);
}; 